import Constants from 'expo-constants';
import { useGlobalSearchParams, useLocalSearchParams, usePathname, useSegments } from 'expo-router';
import { useMemo } from 'react';

/**
 * Hook to get the current pathname in Expo Router
 * @returns The current pathname (e.g., "/courses/guitar-basics/lesson-1")
 */
export function useCurrentPath(): string {
  return usePathname();
}

/**
 * Hook to get search parameters from the current URL (excluding route parameters)
 * @returns Combined local and global search parameters (actual URL query params only)
 */
export function useSearchParams(): Record<string, string> {
  const globalSearchParams = useGlobalSearchParams();
  
  // Only return global search params, as local search params include route parameters
  // which are not actual URL search parameters
  return useMemo(() => ({
    ...globalSearchParams,
  }), [globalSearchParams]) as Record<string, string>;
}

/**
 * Hook to get route parameters (dynamic segments from file paths)
 * @returns Route parameters like { 'lesson-slug': 'guitar-basics', 'course-slug': 'jazz-101' }
 */
export function useRouteParams(): Record<string, string> {
  const localSearchParams = useLocalSearchParams();
  
  return useMemo(() => ({
    ...localSearchParams,
  }), [localSearchParams]) as Record<string, string>;
}

/**
 * Hook to get current pathname with additional URL components
 * @returns Object with pathname and other URL components
 */
export function useCurrentUrl(): {
  pathname: string;
  searchParams: Record<string, string>;
  fullPath: string;
} {
  const pathname = usePathname();
  const globalSearchParams = useGlobalSearchParams();
  
  // Only use global search params (actual URL query parameters)
  const searchParams = useMemo(() => ({
    ...globalSearchParams,
  }), [globalSearchParams]);
  
  // Build full path with search params
  const fullPath = useMemo(() => {
    const searchString = new URLSearchParams(searchParams as Record<string, string>).toString();
    return searchString ? `${pathname}?${searchString}` : pathname;
  }, [pathname, searchParams]);
  
  return {
    pathname,
    searchParams: searchParams as Record<string, string>,
    fullPath,
  };
}

/**
 * Hook to get the full URL including hostname and search parameters
 * @returns The complete URL (e.g., "https://yourapp.com/courses/guitar-basics/lesson-1?tab=video")
 */
export function useFullUrl(): string {
  const pathname = usePathname();
  const globalSearchParams = useGlobalSearchParams();
  
  return useMemo(() => {
    // Get the base URL from Expo constants
    const baseUrl = Constants.expoConfig?.extra?.baseUrl || 
                   Constants.expoConfig?.hostUri || 
                   'https://openstudiojazz.com'; // Fallback
    
    // Only use global search params (actual URL query parameters)
    const searchParams = {
      ...globalSearchParams,
    };
    
    // Build search string
    const searchString = new URLSearchParams(searchParams as Record<string, string>).toString();
    const fullPath = searchString ? `${pathname}?${searchString}` : pathname;
    
    return `${baseUrl}${fullPath}`;
  }, [pathname, globalSearchParams]);
}

/**
 * Comprehensive hook for URL information
 * @returns Object with all URL components
 */
export function useUrlInfo(): {
  pathname: string;
  fullUrl: string;
  segments: string[];
  searchParams: Record<string, string>;
  routeParams: Record<string, string>;
} {
  const pathname = usePathname();
  const segments = useSegments();
  const fullUrl = useFullUrl();
  const localSearchParams = useLocalSearchParams();
  const globalSearchParams = useGlobalSearchParams();
  
  const searchParams = useMemo(() => ({
    ...globalSearchParams,
  }), [globalSearchParams]);
  
  const routeParams = useMemo(() => ({
    ...localSearchParams,
  }), [localSearchParams]);
  
  return {
    pathname,
    fullUrl,
    segments,
    searchParams: searchParams as Record<string, string>,
    routeParams: routeParams as Record<string, string>,
  };
}
